import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import { useQuery } from '@tanstack/react-query'

import { axiosInstance } from '../../../helpers'
import {
    ApartmentProspectsBrowseResponse,
    EnrichedValue,
    Lead,
    Prospect,
} from '../../../models/Outreach/types'
import { PaginatedLeadsResponse } from '../../../hooks/Outreach/useLeads'
import { PIPELINE_STAGE_LABEL, PipelineStage } from '../LeadsPipeline/types'
import { PersonFileTarget } from './types'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            height: '90vh',
            minHeight: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(1),
    },
    hint: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    dialogContent: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    results: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        marginTop: theme.spacing(1),
    },
    resultKind: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    empty: {
        padding: theme.spacing(3, 0.5),
        color: theme.palette.text.secondary,
        textAlign: 'center',
    },
}))

interface FindPersonDialogProps {
    open: boolean
    onClose: () => void
    onSelect: (target: PersonFileTarget) => void
}

interface SearchHit {
    key: string
    name: string
    email: string
    phone: string
    kindLabel: string
    target: PersonFileTarget
}

function contactValue(
    value: string | EnrichedValue | null | undefined,
): string {
    if (!value) return ''
    if (typeof value === 'string') return value
    return value.value || ''
}

function leadName(lead: Lead): string {
    return (
        lead.identity?.name ||
        lead.prospect_detail?.name ||
        lead.campaign_prospect?.prospect?.name ||
        'Unknown'
    )
}

function leadEmail(lead: Lead): string {
    const nested = lead.campaign_prospect?.prospect?.email
    const nestedValue =
        typeof nested === 'string' ? nested : nested?.value ?? ''
    return (
        lead.identity?.email || lead.prospect_detail?.email || nestedValue || ''
    )
}

function leadPhone(lead: Lead): string {
    return (
        lead.identity?.phone ||
        (typeof lead.prospect_detail?.phone === 'string'
            ? lead.prospect_detail.phone
            : '') ||
        lead.campaign_prospect?.prospect?.phone?.value ||
        ''
    )
}

function leadProspectId(lead: Lead): number | null {
    return (
        lead.prospect_detail?.id ?? lead.campaign_prospect?.prospect?.id ?? null
    )
}

function stageLabel(stage: string | undefined): string {
    if (!stage) return 'Lead'
    if (stage === 'app_signed_pending_review') return 'Applications'
    if (stage === 'lease_sent' || stage === 'lease_signed') return 'Leases'
    return PIPELINE_STAGE_LABEL[stage as PipelineStage] || stage
}

export const FindPersonDialog: React.FC<FindPersonDialogProps> = ({
    open,
    onClose,
    onSelect,
}) => {
    const classes = useStyles()
    const [query, setQuery] = useState('')
    const [debounced, setDebounced] = useState('')

    useEffect(() => {
        if (!open) {
            setQuery('')
            setDebounced('')
            return
        }
        const timer = window.setTimeout(() => {
            setDebounced(query.trim())
        }, 300)
        return () => window.clearTimeout(timer)
    }, [query, open])

    const canSearch = debounced.length >= 2

    const leadsQuery = useQuery<Lead[]>({
        queryKey: ['find-person-leads', debounced],
        queryFn: async () => {
            const res = await axiosInstance.get<PaginatedLeadsResponse>(
                '/outreach/leads/get-leads/',
                {
                    params: {
                        search: debounced,
                        page: 1,
                        page_size: 8,
                    },
                },
            )
            return res.data.results ?? []
        },
        enabled: open && canSearch,
        refetchOnWindowFocus: false,
    })

    const prospectsQuery = useQuery<Prospect[]>({
        queryKey: ['find-person-prospects', debounced],
        queryFn: async () => {
            const res = await axiosInstance.get<ApartmentProspectsBrowseResponse>(
                '/outreach/apartment-prospects/browse/',
                {
                    params: {
                        search: debounced,
                        page: 1,
                        page_size: 8,
                    },
                },
            )
            return res.data.results ?? []
        },
        enabled: open && canSearch,
        refetchOnWindowFocus: false,
    })

    const hits = useMemo<SearchHit[]>(() => {
        const leads = leadsQuery.data ?? []
        const prospects = prospectsQuery.data ?? []
        const leadProspectIds = new Set(
            leads.map(leadProspectId).filter((id): id is number => id != null),
        )

        const leadHits: SearchHit[] = leads.map((lead) => ({
            key: `lead-${lead.id}`,
            name: leadName(lead),
            email: leadEmail(lead),
            phone: leadPhone(lead),
            kindLabel: stageLabel(lead.pipeline_stage),
            target: { lead, leadId: lead.id, userId: lead.user_id ?? null },
        }))

        const prospectHits: SearchHit[] = prospects
            .filter((prospect) => !leadProspectIds.has(prospect.id))
            .map((prospect) => {
                const email = contactValue(prospect.email)
                const phone = contactValue(prospect.phone)
                const isProspect = Boolean(email || phone)
                return {
                    key: `prospect-${prospect.id}`,
                    name: prospect.name || 'Unknown',
                    email,
                    phone,
                    kindLabel: isProspect ? 'Prospect' : 'Target',
                    target: { prospect },
                }
            })

        return [...leadHits, ...prospectHits].slice(0, 12)
    }, [leadsQuery.data, prospectsQuery.data])

    const isLoading =
        canSearch && (leadsQuery.isLoading || prospectsQuery.isLoading)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className={classes.dialog}
        >
            <DialogTitle disableTypography>
                <Box className={classes.titleRow}>
                    <Box>
                        <Typography variant="h6">Find person</Typography>
                        <Typography variant="body2" className={classes.hint}>
                            Search everyone this property knows — targets,
                            prospects, applicants, and residents.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <TextField
                    autoFocus
                    fullWidth
                    variant="outlined"
                    placeholder="Name, email, or phone"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && hits[0]) {
                            onSelect(hits[0].target)
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box className={classes.results}>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : !canSearch ? (
                        <Typography className={classes.empty}>
                            Type at least two characters to search.
                        </Typography>
                    ) : hits.length === 0 ? (
                        <Typography className={classes.empty}>
                            No one matches “{debounced}”.
                        </Typography>
                    ) : (
                        <List>
                            {hits.map((hit) => (
                                <ListItem
                                    key={hit.key}
                                    button
                                    onClick={() => onSelect(hit.target)}
                                >
                                    <ListItemText
                                        primary={hit.name}
                                        secondary={
                                            [hit.email, hit.phone]
                                                .filter(Boolean)
                                                .join(' · ') || 'No contact yet'
                                        }
                                    />
                                    <Typography className={classes.resultKind}>
                                        {hit.kindLabel}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}
